import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAnaOverview,
  fetchInstagramPolicies,
  saveInstagramPolicy,
  setAnaChannelMode
} from '../services/ana-channels-api.js';

const MODE_META = {
  active: { label: 'Activo', detail: 'Ana actúa y puede responder en producción.' },
  dry_run: { label: 'Prueba', detail: 'Ana analiza y registra qué haría, pero no publica ni envía.' },
  disabled: { label: 'Apagado', detail: 'Ana no interviene en esta superficie.' }
};

const POLICY_META = {
  ANA: { label: 'Ana atiende', detail: 'Ana evalúa cada turno según el contexto.' },
  PERSONAL: { label: 'Contexto personal', detail: 'Es una pista contextual; no bloquea una consulta comercial futura.' },
  PAUSADO: { label: 'Pausado', detail: 'Ana no interviene mientras la pausa esté vigente.' },
  NO_ANA: { label: 'No atender con Ana', detail: 'Exclusión explícita: Ana no responde automáticamente en redes sociales.' }
};

const CHANNEL_INFO = {
  whatsapp: {
    title: 'WhatsApp',
    description: 'Atención de conversaciones de WhatsApp conectadas a Ana.'
  },
  instagram_dm: {
    title: 'Instagram · mensajes privados',
    description: 'Ana atiende a las personas que escriben por DM. Si respondés manualmente, la conversación se pausa para Ana.'
  },
  instagram_public_comments: {
    title: 'Instagram · comentarios públicos',
    description: 'Ana evalúa comentarios en reels y publicaciones como conversación pública. Puede responder, callarse o derivar según el contexto.'
  },
  instagram_keywords: {
    title: 'Instagram · palabras clave',
    description: 'Automatizaciones explícitas por palabra, reel o publicación, con respuesta pública y/o mensaje privado.'
  },
  instagram_followers: {
    title: 'Instagram · nuevos seguidores',
    description: 'Saludo automático a nuevos seguidores. Por ahora se mantiene apagado.'
  },
  email: {
    title: 'Correo',
    description: 'Canal de correo para Ana. Solo podrá activarse cuando la integración esté operativa.'
  },
  instagram: {
    title: 'Instagram',
    description: 'Control heredado de Instagram. Se reemplaza por controles separados.'
  }
};

const ORDER = [
  'whatsapp',
  'instagram_dm',
  'instagram_public_comments',
  'instagram_keywords',
  'instagram_followers',
  'email'
];

function formatDate(value) {
  if (!value) return 'Sin cambios registrados';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('es-CL');
}

function statusText(channel) {
  if (channel.configured === false) return 'No configurado';
  if (channel.healthy === true) return 'Integración operativa';
  if (channel.healthy === false) return 'Revisar integración';
  return 'Estado no informado';
}

function policyName(record) {
  return record.displayName || record.username || record.userId || 'Sin identificar';
}

function ChannelCard({ channel, saving, onChange }) {
  const info = CHANNEL_INFO[channel.channel] || { title: channel.label || channel.channel, description: '' };
  const blocked = channel.configured === false && (channel.channel === 'email' || channel.channel === 'instagram_followers');
  const mode = MODE_META[channel.mode] || MODE_META.disabled;

  return (
    <article
      style={{
        padding: 18,
        border: '1px solid rgba(127,127,127,.28)',
        borderRadius: 16,
        display: 'grid',
        gap: 14,
        background: 'rgba(127,127,127,.035)'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 220, flex: 1 }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 19 }}>{info.title}</h2>
          <p style={{ margin: 0, opacity: 0.76, lineHeight: 1.5 }}>{info.description}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <strong>{mode.label}</strong>
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 3 }}>{statusText(channel)}</div>
        </div>
      </div>

      {channel.note ? (
        <div style={{ padding: 12, borderRadius: 10, background: 'rgba(127,127,127,.08)', fontSize: 13 }}>
          {channel.note}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 320px) 1fr', gap: 14, alignItems: 'center' }}>
        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Modo</span>
          <select
            value={channel.mode}
            disabled={saving || blocked}
            onChange={(event) => onChange(channel.channel, event.target.value)}
          >
            <option value="active">Activo</option>
            <option value="dry_run">Prueba · no envía</option>
            <option value="disabled">Apagado</option>
          </select>
        </label>
        <div style={{ fontSize: 13, opacity: 0.72 }}>{mode.detail}</div>
      </div>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, opacity: 0.66 }}>
        <span>Último cambio: {formatDate(channel.updatedAt)}</span>
        {channel.updatedBy ? <span>Por: {channel.updatedBy}</span> : null}
        {channel.stats && typeof channel.stats.keywordRules === 'number' ? (
          <span>Reglas activas: {channel.stats.keywordRules}</span>
        ) : null}
        {channel.stats && typeof channel.stats.excluded === 'number' ? (
          <span>Excluidos de Ana: {channel.stats.excluded}</span>
        ) : null}
      </div>

      {channel.channel === 'instagram_keywords' ? (
        <div>
          <Link to="/instagram-automatizaciones">Administrar palabras clave →</Link>
        </div>
      ) : null}
    </article>
  );
}

export default function AnaChannelsPage() {
  const [overview, setOverview] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [policySaving, setPolicySaving] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function reload() {
    setLoading(true);
    setError('');
    try {
      const [nextOverview, nextPolicies] = await Promise.all([
        fetchAnaOverview(),
        fetchInstagramPolicies()
      ]);
      setOverview(nextOverview);
      setPolicies(nextPolicies);
    } catch (err) {
      setError(err && err.message ? err.message : 'No se pudo cargar el centro de control de Ana.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const channels = useMemo(() => {
    const source = Array.isArray(overview && overview.channels) ? overview.channels : [];
    return [...source].sort((a, b) => {
      const ai = ORDER.indexOf(a.channel);
      const bi = ORDER.indexOf(b.channel);
      return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
    });
  }, [overview]);

  const sortedPolicies = useMemo(() => [...policies].sort((a, b) => {
    if (a.policy === 'NO_ANA' && b.policy !== 'NO_ANA') return -1;
    if (b.policy === 'NO_ANA' && a.policy !== 'NO_ANA') return 1;
    return policyName(a).localeCompare(policyName(b), 'es');
  }), [policies]);

  async function changeMode(channel, mode) {
    setSaving(channel);
    setError('');
    setNotice('');
    try {
      await setAnaChannelMode(channel, mode);
      setNotice(`${CHANNEL_INFO[channel]?.title || channel}: ${MODE_META[mode]?.label || mode}.`);
      await reload();
    } catch (err) {
      setError(err && err.message ? err.message : 'No se pudo cambiar el modo.');
    } finally {
      setSaving('');
    }
  }

  async function changePolicy(record, policy) {
    setPolicySaving(record.userId);
    setError('');
    setNotice('');
    try {
      await saveInstagramPolicy(record.userId, {
        policy,
        displayName: record.displayName || '',
        username: record.username || '',
        reason: policy === 'NO_ANA' ? 'excluded_from_admin' : 'updated_from_admin'
      });
      setNotice(`${policyName(record)}: ${POLICY_META[policy]?.label || policy}.`);
      await reload();
    } catch (err) {
      setError(err && err.message ? err.message : 'No se pudo actualizar la política de esta persona.');
    } finally {
      setPolicySaving('');
    }
  }

  const summary = overview && overview.summary ? overview.summary : {};
  const excludedCount = policies.filter((item) => item.policy === 'NO_ANA').length;

  return (
    <main style={{ display: 'grid', gap: 24, paddingBottom: 48 }}>
      <header>
        <p className="analytics-eyebrow">Centro de control</p>
        <h1 style={{ marginBottom: 8 }}>Ana · Canales</h1>
        <p style={{ margin: 0, maxWidth: 900, opacity: 0.76, lineHeight: 1.55 }}>
          Acá podés ver y administrar dónde interviene Ana. Instagram está separado por mensajes privados,
          comentarios públicos, palabras clave y nuevos seguidores para que una función no active las demás.
        </p>
      </header>

      <section style={{ padding: 16, border: '1px solid rgba(127,127,127,.25)', borderRadius: 14 }}>
        <strong>Situación definida para Instagram</strong>
        <p style={{ margin: '8px 0 0', opacity: 0.76 }}>
          Podcast continúa en ManyChat por ahora. Nuevos seguidores queda apagado. Los comentarios públicos usan una política más conservadora que los mensajes privados.
        </p>
      </section>

      {error ? <div role="alert" style={{ padding: 14, border: '1px solid currentColor', borderRadius: 12 }}>{error}</div> : null}
      {notice ? <div role="status" style={{ padding: 14, border: '1px solid currentColor', borderRadius: 12 }}>{notice}</div> : null}

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
        {[
          ['Activos', summary.active ?? 0],
          ['En prueba', summary.dryRun ?? 0],
          ['Apagados', summary.disabled ?? 0],
          ['Integraciones sanas', summary.healthy ?? 0],
          ['No atender con Ana', excludedCount]
        ].map(([label, value]) => (
          <div key={label} style={{ padding: 16, border: '1px solid rgba(127,127,127,.25)', borderRadius: 14 }}>
            <div style={{ fontSize: 12, opacity: 0.68 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4 }}>{value}</div>
          </div>
        ))}
      </section>

      <section style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Canales y superficies</h2>
          <button type="button" onClick={() => void reload()} disabled={loading}>{loading ? 'Cargando…' : 'Actualizar'}</button>
        </div>

        {!loading && channels.length === 0 ? (
          <div style={{ padding: 18, border: '1px dashed rgba(127,127,127,.4)', borderRadius: 14 }}>
            No se encontraron controles de Ana.
          </div>
        ) : null}

        {channels.map((channel) => (
          <ChannelCard
            key={channel.channel}
            channel={channel}
            saving={saving === channel.channel}
            onChange={(id, mode) => void changeMode(id, mode)}
          />
        ))}
      </section>

      <section style={{ display: 'grid', gap: 14, padding: 18, border: '1px solid rgba(127,127,127,.25)', borderRadius: 14 }}>
        <div>
          <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Personas y atención social</h2>
          <p style={{ margin: 0, opacity: .74, lineHeight: 1.5 }}>
            <b>No atender con Ana</b> es una exclusión explícita: los mensajes siguen visibles para contexto y atención humana, pero Ana no responde automáticamente. <b>Contexto personal</b> no bloquea a la persona; solo ayuda a interpretar cada turno.
          </p>
        </div>

        {!loading && sortedPolicies.length === 0 ? (
          <div style={{ padding: 14, border: '1px dashed rgba(127,127,127,.35)', borderRadius: 12, opacity: .75 }}>
            Todavía no hay políticas individuales registradas. Las exclusiones se administran desde este panel; los canales comerciales no aceptan instrucciones administrativas.
          </div>
        ) : null}

        {sortedPolicies.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Persona', 'Usuario / referencia', 'Política', 'Qué significa', 'Último cambio'].map((label) => (
                    <th key={label} style={{ textAlign: 'left', padding: '10px 8px', fontSize: 12, opacity: .68, borderBottom: '1px solid rgba(127,127,127,.2)' }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedPolicies.map((record) => {
                  const meta = POLICY_META[record.policy] || POLICY_META.ANA;
                  return (
                    <tr key={record.userId}>
                      <td style={{ padding: '11px 8px', borderBottom: '1px solid rgba(127,127,127,.12)', fontWeight: 700 }}>{policyName(record)}</td>
                      <td style={{ padding: '11px 8px', borderBottom: '1px solid rgba(127,127,127,.12)', fontSize: 12 }}>{record.username || record.userId}</td>
                      <td style={{ padding: '11px 8px', borderBottom: '1px solid rgba(127,127,127,.12)' }}>
                        <select
                          value={record.policy}
                          disabled={policySaving === record.userId}
                          onChange={(event) => void changePolicy(record, event.target.value)}
                        >
                          <option value="ANA">Ana atiende</option>
                          <option value="PERSONAL">Contexto personal</option>
                          <option value="PAUSADO">Pausado</option>
                          <option value="NO_ANA">No atender con Ana</option>
                        </select>
                      </td>
                      <td style={{ padding: '11px 8px', borderBottom: '1px solid rgba(127,127,127,.12)', fontSize: 12, opacity: .78 }}>{meta.detail}</td>
                      <td style={{ padding: '11px 8px', borderBottom: '1px solid rgba(127,127,127,.12)', fontSize: 12 }}>{formatDate(record.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section style={{ padding: 18, border: '1px solid rgba(127,127,127,.25)', borderRadius: 14 }}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Cómo usar los modos</h2>
        <p style={{ marginBottom: 7 }}><b>Activo:</b> Ana interviene realmente.</p>
        <p style={{ marginBottom: 7 }}><b>Prueba:</b> procesa y deja registro, pero no responde al público.</p>
        <p style={{ marginBottom: 0 }}><b>Apagado:</b> no procesa esa superficie.</p>
      </section>
    </main>
  );
}
